import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatNumber } from '@angular/common';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { DashboardDataService } from 'src/app/services/dashboard-data.service';


interface MeterEntry {
  id?: string;
  name: string;
  import_kwh: number;
  export_kwh?: number;
  solar_generation_kwh?: number;
}

@Component({
  selector: 'app-smart-meter-data',
  templateUrl: './smart-meter-data.component.html',
  styleUrls: ['./smart-meter-data.component.scss'],
})
export class SmartMeterDataComponent implements OnInit, OnDestroy {
  selectedDate!: DateTime;
  today = DateTime.local().startOf('day');
  // Calendar/arrows cap at yesterday; the current day is reachable only via the
  // "Show current data" button.
  maxDate = DateTime.local().startOf('day').minus({ days: 1 });
  todayStr = '';

  prosumers: MeterEntry[] = [];
  consumers: MeterEntry[] = [];

  isLoading = false;

  centerMessage = '';

  /**
   * Whether the "readings can lag" note is shown. It only becomes true when the
   * user explicitly presses "Show current data" and is cleared on any other
   * date change.
   */
  showCurrentDataNote = false;

  private subs = new Subscription();

  constructor(private dashboardData: DashboardDataService) {}

  ngOnInit(): void {
    // Default the selector to yesterday: current-day readings can lag, so the
    // last complete day is shown first.
    this.selectedDate = this.today.minus({ days: 1 });
    this.todayStr = this.selectedDate.toISODate() ?? '';
    this.fetchForSelectedDate();
  }

  /** True when the selected date is the current day. */
  get isShowingToday(): boolean {
    return this.selectedDate.hasSame(this.today, 'day');
  }

  /**
   * Jumps the selector to the current day and surfaces the lag note. This is the
   * only path that shows the note.
   */
  showCurrentData(): void {
    this.selectedDate = this.today;
    this.todayStr = this.selectedDate.toISODate() ?? '';
    this.centerMessage = '';
    this.showCurrentDataNote = true;
    this.fetchForSelectedDate();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  prevDay() {
    this.centerMessage = '';
    this.showCurrentDataNote = false;
    this.selectedDate = this.selectedDate.minus({ days: 1 });
    this.todayStr = this.selectedDate.toISODate() ?? '';
    this.fetchForSelectedDate();
  }

  nextDay() {
  const next = this.selectedDate.plus({ days: 1 });

  // The arrows cannot reach the current day; that is button-only.
  if (next > this.maxDate) {
    return; // simply do nothing
  }

  this.centerMessage = '';
  this.showCurrentDataNote = false;
  this.selectedDate = next;
  this.todayStr = this.selectedDate.toISODate() ?? '';
  this.fetchForSelectedDate();
}

  onDateChange(ev: any) {
    if (ev.value) {
      this.selectedDate = DateTime.fromJSDate(ev.value).startOf('day');
      this.todayStr = this.selectedDate.toISODate() ?? '';
      this.centerMessage = '';
      this.showCurrentDataNote = false;
      this.fetchForSelectedDate();
    }
  }

  onDateInput(ev: Event) {
    const v = (ev.target as HTMLInputElement).value;
    if (!v) return;
    const selected = DateTime.fromISO(v).startOf('day');

    const todayStart = DateTime.local().startOf('day');
    if (selected > todayStart) {
    
      this.selectedDate = selected;
      this.todayStr = selected.toISODate() ?? '';
      this.prosumers = [];
      this.consumers = [];
      this.isLoading = false;
      this.centerMessage = 'Future dates are not supported.';
      this.showCurrentDataNote = false;
      return;
    }

    this.centerMessage = '';
    this.showCurrentDataNote = false;
    this.selectedDate = selected;
    this.fetchForSelectedDate();
  }

  private computeOffsetForSelectedDate(): number {
    const todayStart = DateTime.local().startOf('day');
    const diff = Math.round(this.selectedDate.diff(todayStart, 'days').days);
    if (diff > 0) {
      return 0;
    }
    return Math.abs(diff);
  }

  private mapToArray(obj?: { [k: string]: any }): MeterEntry[] {
    if (!obj) return [];
    return Object.keys(obj).map((k, idx) => ({
      id: k,
      name: obj[k].name ?? `#${idx + 1}`,
      import_kwh: Number(obj[k].import_kwh) || 0,
      export_kwh: obj[k].export_kwh !== undefined ? Number(obj[k].export_kwh) : undefined,
      solar_generation_kwh: obj[k].solar_generation_kwh !== undefined ? Number(obj[k].solar_generation_kwh) : undefined,
    }));
  }

  fetchForSelectedDate() {
   
    const todayStart = DateTime.local().startOf('day');
    if (this.selectedDate > todayStart) {
      this.prosumers = [];
      this.consumers = [];
      this.isLoading = false;
      this.centerMessage = 'Future dates are not supported.'; 
      return;
    }

    this.isLoading = true;
    this.centerMessage = '';
    this.prosumers = [];
    this.consumers = [];

    const offset = this.computeOffsetForSelectedDate();

    const sub = this.dashboardData.getImportExport(offset).subscribe({
      next: (res: any) => {
        this.prosumers = this.mapToArray(res?.Prosumer);
        this.consumers = this.mapToArray(res?.Consumer);
        this.isLoading = false;
     
      },
      error: (err) => {
        console.error('Failed to fetch import-export:', err);
        if (err?.status === 401) {
          this.centerMessage = 'Unauthorized. Please login.';
        } else if (err?.status === 400) {
          this.centerMessage = err?.error?.detail || 'Bad request.';
        } else {
          this.centerMessage = err?.error?.detail || 'Failed to load meter data. Try again.';
        }
        this.isLoading = false;
      },
    });

    this.subs.add(sub);
  }

  /**
   * Formats an energy value for display.
   * Returns '--' when the value is null, undefined, NaN, or zero —
   * all of which indicate that no meter reading has been received yet.
   * Handles values arriving as either a number or a numeric string (e.g. "0.0").
   */
  formatEnergy(val: any, digitsInfo: string = '1.2-2'): string {
    if (val === null || val === undefined) {
      return '--';
    }
    const num = typeof val === 'string' ? parseFloat(val) : Number(val);
    if (isNaN(num) || num === 0) {
      return '--';
    }
    return formatNumber(num, 'en-US', digitsInfo);
  }
}
